import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { generateTableOfContents, startFullContentGeneration, getContentGenerationStatus, downloadContentFile, markContentRetrieved } from '../../services/chatApi';
import { Loader2, AlertCircle, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown'; 


// --- Helper function (place outside the component) ---


const convertContentJsonToString = (contentJson) => {
    if (typeof contentJson !== 'object' || contentJson === null) {
        return '';
    }
    let fullText = '';
    Object.keys(contentJson).forEach(chapterTitle => {
        fullText += `${chapterTitle}\n\n`;
        const chapterContent = contentJson[chapterTitle];
        if (chapterContent.intro_chapter) {
            fullText += `${chapterContent.intro_chapter}\n\n`;
        }
        Object.keys(chapterContent)
            .filter(key => key !== 'intro_chapter')
            .forEach(sectionTitle => {
                fullText += `${sectionTitle}\n\n`;
                const sectionContent = chapterContent[sectionTitle];
                if (sectionContent && sectionContent.content) {
                    fullText += `${sectionContent.content}\n\n`;
                }
            });
    });
    return fullText.trim();
};

const customSortKeys = (a, b) => {
    // const regex = /(Chapter|Section)\s*([\d.]+)/;
    const regex = /(Chapter|Section|Kapitel|Abschnitt|Chapitre)\s*([\d.]+)/i;
    const matchA = a.match(regex);
    const matchB = b.match(regex);

    if (matchA && matchB) {
        // Split version numbers like "2.1" into parts [2, 1]
        const partsA = matchA[2].split('.').map(Number);
        const partsB = matchB[2].split('.').map(Number);
        
        // Compare parts level by level
        for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
            if (partsA[i] !== partsB[i]) {
                return partsA[i] - partsB[i];
            }
        }
        return partsA.length - partsB.length;
    }
    // Fallback for non-matching keys
    return a.localeCompare(b);
};

const cleanContent = (title, content) => {
    if (!title || !content) return content || '';

    // Normalize both strings for a more robust comparison
    // This removes markdown, trims whitespace, and converts to lowercase
    const normalize = (str) => str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().trim();
    
    const normalizedTitle = normalize(title);

    // Split content into lines
    const lines = content.trim().split('\n');
    const firstLine = lines[0] || '';
    const normalizedFirstLine = normalize(firstLine);

    // If the normalized first line of the content is the same as the normalized title,
    // it's a duplicate. Return the rest of the content.
    if (normalizedFirstLine === normalizedTitle) {
        return lines.slice(1).join('\n').trim();
    }

    // Otherwise, the content is fine as is.
    return content;
};

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};


const GenerateContentTool = () => {
    const [isDownloading, setIsDownloading] = useState(false);

    const { t, i18n } = useTranslation();
    const { user, setCredits } = useAuth();

    // State
    const [step, setStep] = useState(1); // 1: Form, 2: Edit ToC, 3: Generating, 4: Result
    const [formData, setFormData] = useState({
        topic: '',
        num_chapters: 4,
        num_sections: 4,
        tone: 'Professional',
        instructions: ''
    });
    const [jobData, setJobData] = useState(null); // Will hold the full job object from backend
    const [editableToc, setEditableToc] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasCopied, setHasCopied] = useState(false);
    
    const pollingIntervalRef = useRef(null);

    useEffect(() => {
        // Cleanup polling on unmount
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, []);

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleDownload = async (format) => {
        setIsDownloading(true);
        try {
            await downloadContentFile(jobData.full_content_json, format, jobData.topic);
        } catch (error) {
            // You should have a state to show errors to the user
            console.error("Download error:", error);
            setError(error.message);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleGenerateToc = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            const response = await generateTableOfContents(formData);
            setJobData(response);
            setEditableToc(response.table_of_contents_text);
            setStep(2); // Move to Edit ToC step
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };
    
    const pollForContent = (jobId) => {
        pollingIntervalRef.current = setInterval(async () => {
            try {
                const statusResult = await getContentGenerationStatus(jobId);
                setJobData(statusResult); // Update job data with progress
                if (statusResult.status === 'SUCCESS') {
                    clearInterval(pollingIntervalRef.current);
                    if (statusResult.new_credits !== undefined) {
                        await setCredits(statusResult.new_credits);
                    }

                    if (statusResult.id) {
                        try {
                            await markContentRetrieved(statusResult.id);
                            console.log("Content generation record cleared from DB");
                        } catch (err) {
                            console.warn("Failed to clear content generation from DB:", err);
                        }
                    }
                    setStep(4); // Move to Result step
                    setIsLoading(false);
                } else if (statusResult.status === 'FAILURE') {
                    clearInterval(pollingIntervalRef.current);
                    setError(statusResult.error_message || t('generateContentTool.errorGeneric'));
                    setStep(2); // Go back to editing ToC on failure
                    setIsLoading(false);
                }
            } catch (err) {
                clearInterval(pollingIntervalRef.current);
                setError(err.message);
                setStep(2);
                setIsLoading(false);
            }
        }, 3000);
    };

    const handleGenerateFullContent = async () => {
        setIsLoading(true);
        setError(null);
        setStep(3); // Move to Generating step
        try {
            await startFullContentGeneration(jobData.id, editableToc);
            pollForContent(jobData.id);
        } catch (err) {
            setError(err.message);
            setStep(2); // Go back to editing on failure to start
            setIsLoading(false);
        }
    };

    // --- MODIFIED handleCopy ---
    const handleCopy = () => {
        const contentString = convertContentJsonToString(jobData?.full_content_json);
        if (contentString) {
            navigator.clipboard.writeText(contentString);
            setHasCopied(true);
            setTimeout(() => setHasCopied(false), 2000);
        }
    };
    
    // --- Render Methods for each step ---
    const renderStep1_Form = () => (
        <form onSubmit={handleGenerateToc} className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-brand-orange">{t('generateContentTool.title')}</h3>
            <p className="mt-2 text-brand-dark">{t('generateContentTool.description')}</p>

            <div className="mt-6 space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-2 text-brand-dark">
                        {t('generateContentTool.topicLabel')}
                    </label>
                    <input 
                        name="topic" 
                        value={formData.topic} 
                        onChange={handleFormChange} 
                        placeholder={t('generateContentTool.topicPlaceholder')}
                        className="w-full p-3 text-brand-dark border border-slate-400 rounded-md focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-2 text-brand-dark">
                            {t('generateContentTool.chaptersLabel')}
                        </label>
                        <input 
                            type="number" 
                            name="num_chapters" 
                            value={formData.num_chapters} 
                            onChange={handleFormChange} 
                            min="1" 
                            max="6"
                            className="w-full p-3 text-brand-dark border border-slate-400 rounded-md focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium mb-2 text-brand-dark">
                            {t('generateContentTool.sectionsLabel')}
                        </label>
                        <input 
                            type="number" 
                            name="num_sections" 
                            value={formData.num_sections} 
                            onChange={handleFormChange} 
                            min="1" 
                            max="6"
                            className="w-full p-3 text-brand-dark border border-slate-400 rounded-md focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2 text-brand-dark">
                        {t('generateContentTool.toneLabel')}
                    </label>
                    <select 
                        name="tone" 
                        value={formData.tone} 
                        onChange={handleFormChange}
                        className="w-full p-3 text-brand-dark border border-slate-400 rounded-md focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    >
                        <option value="Professional">{t('generateContentTool.toneProfessional')}</option>
                        <option value="Casual">{t('generateContentTool.toneCasual')}</option>
                        <option value="Academic">{t('generateContentTool.toneAcademic')}</option>
                        <option value="Formal">{t('generateContentTool.toneFormal')}</option>
                        <option value="Informal">{t('generateContentTool.toneInformal')}</option>
                        <option value="Instructional">{t('generateContentTool.toneInstructional')}</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2 text-brand-dark">
                        {t('generateContentTool.instructionsLabel')}
                    </label>
                    <textarea 
                        name="instructions" 
                        value={formData.instructions} 
                        onChange={handleFormChange}
                        placeholder={t('generateContentTool.instructionsPlaceholder')}
                        className="w-full p-3 text-brand-dark border border-slate-400 rounded-md min-h-[100px] focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    />
                </div>
            </div>

            <button 
                type="submit" 
                disabled={isLoading || !formData.topic.trim()}
                className="mt-6 px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
                {isLoading ? (
                    <>
                        <Loader2 className="animate-spin inline mr-2" />
                        {t('generateContentTool.generatingToc')}
                    </>
                ) : t('generateContentTool.generateTocButton')}
            </button>
        </form>
    );

    const renderStep2_EditToc = () => (
        <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-brand-orange">{t('generateContentTool.editTocTitle')}</h3>
            <p className="mt-2 text-brand-dark">{t('generateContentTool.editTocDescription')}</p>

            <div className="mt-6">
                <label className="block text-sm font-medium mb-2 text-brand-dark">
                    {t('generateContentTool.tocLabel')}
                </label>
                <textarea 
                    value={editableToc}
                    onChange={(e) => setEditableToc(e.target.value)}
                    className="w-full p-3 text-brand-dark border border-slate-400 rounded-md min-h-[300px] focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                />
            </div>

            <div className="flex gap-4 mt-4">
                <button 
                    onClick={handleGenerateFullContent} 
                    disabled={isLoading} 
                    className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed"
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="animate-spin inline mr-2" />
                            {t('generateContentTool.generatingContent')}
                        </>
                    ) : t('generateContentTool.generateContentButton')}
                </button>
                
                <button 
                    onClick={() => setStep(1)}
                    disabled={isLoading}
                    className="px-6 py-2 border border-slate-400 hover:bg-slate-200 text-brand-dark font-semibold rounded-md transition-colors disabled:opacity-50"
                >
                    {t('generateContentTool.backButton')}
                </button>
            </div>
        </div>
    );
    
    const renderStep3_Generating = () => (
        <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-brand-orange">{t('generateContentTool.generatingTitle')}</h3>
            <p className="mt-2 text-brand-dark">{t('generateContentTool.generatingDescription')}</p>

            <div className="mt-8 flex flex-col items-center justify-center py-12">
                <Loader2 className="h-12 w-12 text-brand-orange animate-spin" />
                <div className="mt-4 text-center">
                    <p className="text-slate-600 font-medium">
                        {jobData?.generation_progress || 0}% {t('generateContentTool.complete')}
                    </p>
                    {jobData?.current_progress_message && (
                        <p className="text-slate-500 text-sm mt-1">
                            {jobData.current_progress_message}
                        </p>
                    )}
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2.5 mt-2">
                    <div className="bg-brand-orange h-2.5 rounded-full" style={{ width: `${jobData?.generation_progress || 0}%` }}></div>
                </div>
            </div>

            {jobData?.table_of_contents_text && (
                <div className="mt-8">
                    <h4 className="text-xl font-semibold">{t('generateContentTool.previewTitle')}</h4>
                    <div className="mt-4 p-4 bg-slate-50 text-brand-dark border border-slate-300 rounded-md whitespace-pre-wrap font-sans text-sm">
                        {jobData.table_of_contents_text}
                    </div>
                </div>
            )}
        </div>
    );
    
    // --- MODIFIED renderStep4_Result ---
    const renderStep4_Result = () => {
        const contentJson = jobData?.full_content_json;
        const isContentValid = typeof contentJson === 'object' && contentJson !== null && Object.keys(contentJson).length > 0;

        return (
            <div className="max-w-4xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange">{t('generateContentTool.resultTitle')}</h3>
                <p className="mt-2 text-brand-dark">{t('generateContentTool.resultDescription')}</p>
                <div className="my-6 flex flex-wrap gap-4">
                    <button
                        onClick={() => handleDownload('docx')}
                        disabled={isDownloading}
                        className="p-2 bg-slate-200 hover:bg-brand-orange rounded-md transition-colors z-10 disabled:opacity-50"
                        title="Download DOCX"
                    >
                        {isDownloading ? (
                            <Loader2 size={16} className="animate-spin text-slate-700" />
                        ) : (
                            <span className="text-xs font-semibold text-slate-700">DOCX</span>
                        )}
                    </button>
                    
                </div>
                {isContentValid && (
                    <div className="mt-8">
                        <h4 className="text-xl font-semibold mb-4">{t('generateContentTool.contentTitle')}</h4>
                        <div className="relative mt-4 bg-brand-white p-6 border border-slate-700 rounded-md font-sans text-sm text-brand-dark">
                            <button
                                onClick={handleCopy}
                                className="absolute top-2 right-2 p-1.5 bg-slate-200 hover:bg-brand-orange rounded-md transition-colors z-10"
                                title={t('generateContentTool.copyButton')}
                            >
                                {hasCopied ? <Check size={16} className="text-green-500" /> : <Copy size={16} className="text-slate-700" />}
                            </button>

                            <div className="prose prose-slate max-w-none">
                                {Object.keys(contentJson).sort(customSortKeys).map(chapterTitle => {
                                    // Clean the intro content
                                    const cleanedIntro = cleanContent(
                                        chapterTitle,
                                        contentJson[chapterTitle].intro_chapter
                                    );

                                    return (
                                        <div key={chapterTitle}>
                                            <ReactMarkdown>{`# ${chapterTitle}`}</ReactMarkdown>
                                            
                                            {cleanedIntro && (
                                                <ReactMarkdown
                                                    components={{
                                                        p: ({node, ...props}) => <p className="italic text-slate-600" {...props} />
                                                    }}
                                                >
                                                    {cleanedIntro}
                                                </ReactMarkdown>
                                            )}
        
                                            {Object.keys(contentJson[chapterTitle])
                                                .filter(key => key !== 'intro_chapter')
                                                .sort(customSortKeys)
                                                .map(sectionTitle => {
                                                    // --- THE FIX IS HERE ---
                                                    // We call our new helper to clean the content before rendering it.
                                                    const cleanedSectionContent = cleanContent(
                                                        sectionTitle, 
                                                        contentJson[chapterTitle][sectionTitle].content
                                                    );
                                                    
                                                    return (
                                                        <div key={sectionTitle}>
                                                            <ReactMarkdown>{`## ${sectionTitle}`}</ReactMarkdown>
                                                            <ReactMarkdown>
                                                                {cleanedSectionContent}
                                                            </ReactMarkdown>
                                                        </div>
                                                    );
                                                })
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
    
                <div className="mt-6 flex gap-4">
                     {/* ... your "Start New" button */}
                </div>
            </div>
        );
    };



    // Check subscription and credits before rendering main content
    if (user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) {
        return (
            <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
                <SubscribePrompt />
            </div>
        );
    }
    if (user?.credits <= 0) {
        return (
            <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
                <PurchaseCreditsPrompt />
            </div>

         );
    }

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
        
            {error && (
                <div className="max-w-4xl mx-auto mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-center">
                    <AlertCircle size={16} className="mr-2 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}
            
            {step === 1 && renderStep1_Form()}
            {step === 2 && renderStep2_EditToc()}
            {step === 3 && renderStep3_Generating()}
            {step === 4 && renderStep4_Result()}

        </div>

    );
};

export default GenerateContentTool;
